/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const VARNAME = `${feature.getSettingValue('VARNAME')}`;
    let DATATYPE = feature.getSettingValue('DATATYPE');
    const FEATURE = feature.getSettingValue('FEATURE');
    const MAXIMUM = feature.getSettingValue('MAXIMUM');
    const POLL_INTERVAL = feature.getSettingValue('POLL_INTERVAL');
    const linkedFeature = project.getFeature(FEATURE);

    let defaultValue = feature.getSettingValue('DEFAULT');
    if (DATATYPE === 'string') {
        defaultValue = `"${defaultValue}"`;
    } else if (defaultValue === '') {
        defaultValue = '0';
    }
    if (DATATYPE === TibboDataType.FLOAT && defaultValue.indexOf('.') === -1) {
        defaultValue += '.0';
    }
    // data type of datetime is dword
    if (DATATYPE === 'datetime') {
        DATATYPE = 'dword';
    }
    if (DATATYPE === 'time') {
        DATATYPE = 'float';
    }

    let stringSize = '';
    if (DATATYPE === TibboDataType.STRING) {
        // get linked setting
        const linkedFeature = project.getFeature(FEATURE);
        if (linkedFeature && linkedFeature.getSettingValue('MAX') !== '') {
            stringSize = `(${linkedFeature.getSettingValue('MAX')})`;
        }
        if (MAXIMUM !== '') {
            stringSize = `(${MAXIMUM})`;
        }
    }

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
        simulator.emit({
            eventType: 'var_set',
            name: "${VARNAME}",
            value: ${VARNAME},
        });
        `,
    });

    // optimization for declaring changed handler
    let hasChangedHandler = false;
    for (let i = 0; i < project.features.length; i++) {
        const tmpFeature = project.features[i];
        let linkedFeatureName = '';
        if (tmpFeature.id === 'node_variable_changed') {
            if (tmpFeature.getSettingValue('VARIABLE') === feature.getID()) {
                hasChangedHandler = true;
                break;
            }
        } else if (tmpFeature.id === 'node_timer_completed') {
            const timer = project.getFeature(tmpFeature.getSettingValue('TIMER'));
            if (timer !== undefined) {
                linkedFeatureName = timer.getSettingValue('TIMER_NAME');
            }
        } else if (['tibbit_39_1', 'tibbit_39_2', 'tibbit_39_3', 'tibbit_39_4'].includes(tmpFeature.id)) {
            linkedFeatureName = tmpFeature.getSettingValue('L1NAME');
        } else if (['tibbit_40_1', 'tibbit_40_2', 'tibbit_40_3', 'tibbit_40_4'].includes(tmpFeature.id)) {
            linkedFeatureName = tmpFeature.getSettingValue('L1NAME');
        }
        if (linkedFeatureName !== '') {
            if (linkedFeatureName === VARNAME) {
                hasChangedHandler = true;
                break;
            }
        }
        // if cgg or web dashboard, check if variable is used in any of the widgets
        if (['cgg', 'web_dashboard'].includes(tmpFeature.id)) {
            const widgets = tmpFeature.getSettingValue('WIDGETS');
            for (let j = 0; j < widgets.length; j++) {
                if (widgets[j].getSettingValue('VARIABLE') === feature.getID()) {
                    hasChangedHandler = true;
                    break;
                }
            }
        }
    }

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `let ${VARNAME}=${defaultValue};`,
    });

    blocks.push({
        location: 'RESET_VARIABLES',
        blockType: BlockType.CODE,
        template: `
${VARNAME} = ${defaultValue};
        `,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_update`,
        blockType: BlockType.LOCATION,
        template: `
function var_${VARNAME}_update() {
    /****var_${VARNAME}_update****/
}
            `,
    });

    const MIN = feature.getSettingValue('MINIMUM');
    const MAX = feature.getSettingValue('MAXIMUM');
    let minCode = '';
    let maxCode = '';
    if (MIN !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            minCode = `if (value < ${MIN}) {
            value = ${MIN};
        }`;
        }
    }
    if (MAX !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            maxCode = `if (value > ${MAX}) {
             value = ${MAX};
        }`;
        }
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_update_completed`,
        blockType: BlockType.LOCATION,
        template: `
function var_${VARNAME}_update_completed(value) {
    const value_old = ${VARNAME};
    /****var_${VARNAME}_update_completed****/
    ${minCode}
    ${maxCode}
    ${VARNAME} = value;
    if (value_old != ${VARNAME}) {
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old);` : ''}
        //#if VARIABLES_DEBUG_PRINT
            console.log("VAR> ${VARNAME} changed from " + ${project.convertDataType('value_old', DATATYPE, TibboDataType.STRING)} + " to " +  ${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)} + "\\r\\n")
        //#endif
        simulator.emit({
            eventType: 'var_set',
            name: "${VARNAME}",
            value: ${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)},
        });
    }
    /****var_${VARNAME}_updated****/
}
            `,
    });

    let var_set_inner = '';
    if (linkedFeature && linkedFeature.id === 'stg_setting') {
        var_set_inner = `stg_set("${VARNAME}", ${project.convertDataType('value', DATATYPE, TibboDataType.STRING)})`;
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_set`,
        blockType: BlockType.LOCATION,
        template: `
function var_${VARNAME}_set(value) {
    ${minCode}
    ${maxCode}
    const value_old = ${VARNAME};
    ${VARNAME}=value;
    ${var_set_inner}
    /****var_${VARNAME}_set****/

    if (value_old != ${VARNAME}) {
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old);` : `
        /****var_${VARNAME}_changed****/`}
    }
    simulator.emit({
        eventType: 'var_set',
        name: "${VARNAME}",
        value: ${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)},
    });
}
            `,
    });

    if (hasChangedHandler) {
        blocks.push({
            location: 'FUNCTION_DEFINITIONS',
            id: `var_${VARNAME}_changed`,
            blockType: BlockType.LOCATION,
            template: `
function var_${VARNAME}_changed(value_old) {
    /****var_${VARNAME}_changed****/
}
            `,
        });
    }

    //
    // if ((linkedFeature && linkedFeature.id !== 'stg_setting') || !linkedFeature) {
    //     blocks.push({
    //         location: 'boot_complete',
    //         blockType: BlockType.CODE,
    //         template: `
    //     var_${VARNAME}_update_completed(${defaultValue})`,
    //     });
    // }

    if (POLL_INTERVAL === '') {
        blocks.push({
            location: 'var_poll_timer_elapsed',
            blockType: BlockType.CODE,
            template: `
    /****var_poll_timer_${VARNAME}_elapsed****/`,
        });
    } else {
        const TIMER_NAME = `var_poll_timer_${VARNAME}`;
        const INTERVAL = Number(POLL_INTERVAL) * 1000;
    
        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `let ${TIMER_NAME};`,
        });
    
        blocks.push({
            location: 'RESET_VARIABLES',
            blockType: BlockType.CODE,
            template: `
clearInterval(${TIMER_NAME});
${TIMER_NAME} = null;
        `,
        });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `\t${TIMER_NAME} = setInterval(async () => {
    if (simulator.paused) {
        return;
    }
    /****var_poll_timer_${VARNAME}_elapsed****/
            }, ${INTERVAL});`,
        });
    }

    blocks.push({
        location: 'ON_SIMULATOR_VAR_SET',
        blockType: BlockType.CODE,
        template: `
        case "${VARNAME}":
            ${VARNAME}=event.value;
            simulator.emit({
                eventType: 'var_set',
                name: "${VARNAME}",
                value: ${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)},
            });
        break;`,
    });

    blocks.push({
        location: 'ON_SIMULATOR_SET_VARIABLE',
        blockType: BlockType.CODE,
        template: `
case "${VARNAME}":
    var_${VARNAME}_set(value)
    break;
        `,
    });

    blocks.push({
        location: 'callback_luis_read',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    value=${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)}
    callback_luis_read=true
end if`,
    });

    blocks.push({
        location: 'callback_luis_write',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    ${VARNAME}=${project.convertDataType('value', TibboDataType.STRING, DATATYPE)}
    callback_luis_write=true
end if`,
    });

    return blocks;
};
